import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { AxiosRequestConfig } from 'axios'

import { UpdateUserRequestDTO } from '@/core/api/dtos/request/users/updateUser'
import { userService } from '@/core/api/services/userService'

interface UpdateUserOptions {
	onSuccess?: () => void
	onError?: (error: any) => void
}

export const useUpdateUser = (config?: AxiosRequestConfig) => {
	const queryClient = useQueryClient()

	const updateUserMutation = useMutation({
		mutationFn: ({
			userId,
			data,
		}: {
			userId: number
			data: UpdateUserRequestDTO
		}) => {
			return userService.updateUser(userId, data, config)
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['users'] })
		},
		onError: (error) => {
			// This ensures errors are caught at the mutation level
			// The mutate callbacks will also be called, but this provides a fallback
			console.error('Update user error:', error)
		},
	})

	const updateUser = async (
		userId: number,
		data: UpdateUserRequestDTO,
		options?: UpdateUserOptions
	) => {
		try {
			await updateUserMutation.mutateAsync({ userId, data })
			queryClient.invalidateQueries({ queryKey: ['users'] })
			options?.onSuccess?.()
		} catch (error) {
			// Call error callback if provided, but don't re-throw
			// This prevents double error handling
			if (options?.onError) {
				options.onError(error)
			}
			// Don't re-throw to prevent the error from propagating to outer try-catch
		}
	}

	return {
		updateUser,
		isUpdating: updateUserMutation.isPending,
		error: updateUserMutation.error,
	}
}
